import { colors } from '@/constants';
import { useCart, useSelectedClientScopeWithFallback } from '@/hooks';
import { packingTypes } from '@/shared';
import { PackagesTypesProps, PackagingProductProps, ProductProps } from '@/types';
import { useMemo } from 'react';
import * as S from './styles';

interface Product {
  size?: number;
  showQtd?: boolean;
  product: ProductProps;
  pack: PackagesTypesProps;
  packQty: number;
  setPackage: (val: PackagesTypesProps) => void;
  setPackQuantity: (val: number) => void;
  autoSelectFirst?: boolean;
  canDisablePack?: boolean;
  fullBorder?: boolean;
  /** Quando true, exibe apenas a embalagem correspondente a `pack`/`packQty`, ocultando as demais opções do produto. */
  restrictToCurrent?: boolean;
}

const RenderPackaging = ({
  pack,
  size,
  product,
  packQty,
  setPackage,
  setPackQuantity,
  canDisablePack = true,
  fullBorder = false,
  restrictToCurrent = false,
}: Product) => {
  const { getLimit } = useCart();
  const selectedClientScope = useSelectedClientScopeWithFallback();
  const toNumber = (value: unknown) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const getSegmentCode = (segmento: any) => {
    return toNumber(segmento?.cod_segmento ?? segmento?.COD_SEGMENTO ?? segmento);
  };
  const clientSegs = (Array.isArray(selectedClientScope?.COD_SEGMENTO) ? selectedClientScope.COD_SEGMENTO : [])
    .map((segmento: any) => getSegmentCode(segmento))
    .filter((segmento): segmento is number => segmento != null);
  const clientLoja = toNumber(selectedClientScope?.COD_LOJA);

  const matchesCurrentPack = (embalagem: PackagingProductProps) =>
    pack === 'PK'
      ? embalagem?.QTD_MULTIPLO_VENDA > 1 && embalagem?.QTD_MULTIPLO_VENDA === packQty && pack === embalagem?.SG_EMBALAGEM
      : embalagem?.SG_EMBALAGEM === pack && embalagem?.QTD_EMBALAGEM === packQty;

  const list = useMemo(() => {
    const embs = (product?.EMBALAGEM ? [...product.EMBALAGEM] : []).filter((emb) => {
      const embLoja = toNumber(emb?.COD_LOJA);
      const embSegmento = toNumber(emb?.COD_SEGMENTO);

      const lojaOk = clientLoja == null || embLoja == null || embLoja === clientLoja;
      if (!lojaOk) return false;

      if (!clientSegs.length) return true;
      if (embSegmento == null) return false;
      return clientSegs.includes(embSegmento);
    });
    
    if (!embs.length) {
      const fallbackList = product?.EMBALAGEM || [];
      // Mesmo no fallback (nenhuma embalagem bate loja/segmento do cliente), restrictToCurrent não
      // pode ser ignorado — senão o seletor aberto pelo carrinho pode oferecer uma embalagem
      // diferente da que está de fato na linha.
      if (!restrictToCurrent) return fallbackList.slice(0, 1);
      const restricted = fallbackList.filter(matchesCurrentPack);
      return restricted.length ? restricted : fallbackList.slice(0, 1);
    }

    const orderedList = embs.sort((a, b) => {
      const aQtd = (a.QTD_MULTIPLO_VENDA || 1) * (a.QTD_EMBALAGEM || 1);
      const bQty = (b.QTD_MULTIPLO_VENDA || 1) * (b.QTD_EMBALAGEM || 1);
      return aQtd < bQty ? 1 : -1;
    });

    const filteredList: Record<string, PackagingProductProps> = {};
    for (const item of orderedList) {
      const key = `${item.SG_EMBALAGEM}-${item.COD_SEGMENTO}-${item.QTD_EMBALAGEM}-${item.QTD_MULTIPLO_VENDA}`;
      if (filteredList[key]) continue;
      filteredList[key] = item;
    }

    const dedupedList = Object.values(filteredList);

    if (!restrictToCurrent) return dedupedList;

    // Se nada bater (ex.: loja/segmento do cliente mudou sutilmente desde que o item foi
    // adicionado ao carrinho), preferir mostrar a lista completa a renderizar o seletor vazio.
    const restricted = dedupedList.filter(matchesCurrentPack);
    return restricted.length ? restricted : dedupedList;
  }, [product.EMBALAGEM, clientLoja, clientSegs, restrictToCurrent, pack, packQty]) as PackagingProductProps[];

  const limit = getLimit(product?.COD_PRODUTO);

  return (
    <>
      <S.Row>
        {/* Rever esse map */}
        {list.map((embalagem) => {
          const packing = packingTypes?.[embalagem?.SG_EMBALAGEM];

          const qty = embalagem?.SG_EMBALAGEM === 'PK' ? embalagem?.QTD_MULTIPLO_VENDA : embalagem?.QTD_EMBALAGEM;
          if (!packing || !qty) return null;

          const isActive = matchesCurrentPack(embalagem);
          const rawPrecos: any = product?.PRECO || {};
          const list: any[] = Array.isArray(rawPrecos) ? rawPrecos : Object.values(rawPrecos || {});
          const hasPrice = list.find((p) => {
            if (p?.SG_EMBALAGEM !== packing?.shortName) return false;
            const sameQty = packing?.shortName === 'PK' ? Number(p?.QTD_MULTIPLO_VENDA) === Number(qty) : Number(p?.QTD_EMBALAGEM) === Number(qty);
            if (!sameQty) return false;
            const priceLoja = toNumber(p?.COD_LOJA);
            return clientLoja == null || priceLoja == null || priceLoja === clientLoja;
          });
          const isDisabled = canDisablePack && (limit < qty || !hasPrice);

          return (
            <S.BoxEmbalagem
              size={size}
              $fullBorder={fullBorder}
              $isactive={isActive}
              $unityPack={list.length === 1}
              onClick={() => {
                if (isDisabled) return;

                setPackage(packing?.shortName);
                setPackQuantity(qty);
              }}
              key={`pack-${product.COD_PRODUTO}-${packing?.shortName}-${qty}`}
              data-test={`pack-${product.COD_PRODUTO}-${packing?.shortName}-${qty}`}
            >
              <S.PackImage src={packing?.icon} alt="" />
              {isActive && <S.CheckFill size={24} color={colors.alert.green110} />}

              <S.BoxLabel>
                <S.BoxQtdEmbalagem>
                  <S.QtdEmbalagem>{qty}/un</S.QtdEmbalagem>
                </S.BoxQtdEmbalagem>
              </S.BoxLabel>
            </S.BoxEmbalagem>
          );
        })}
      </S.Row>
    </>
  );
};

export default RenderPackaging;
